import { IonButton, IonIcon, IonInfiniteScroll, IonInfiniteScrollContent, IonItem, IonLabel, IonModal, IonNote, IonToggle } from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import Plots from '../components/Plots';
import { getInactivePlotsList, getInactivePlotsCount, searchPlotsClientSide } from '../services/plot.service';
import LoadingSkeleton from './LoadingSkeleton';
import { funnelOutline } from 'ionicons/icons';
import CustomSearchBox from './CustomSearchBox/CustomSearchBox';
import PlotFilter from './PlotFilter';

// debounce hook
import useDebounce from "../hooks/useDebounce";
import useNearMePlots from '../hooks/useNearMePlots';
import { useRecoilValue } from 'recoil';
import { userProfileState } from '../services/state.service';

const LIMIT = 20;

const InactivePlots: React.FC = () => {
    const userProfile = useRecoilValue(userProfileState);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    const [showFilterModal, setShowFilterModal] = useState(false);
    const [filterData, setFilterData] = useState<any>();
    const [filterCriteria, setFilterCriteria] = useState<any>(null);
    const [plots, setPlots] = useState([] as any);
    const [skip, setSkip] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const debounceSearchText = useDebounce(searchText, 1000);

    const nearMe = useNearMePlots(userProfile, 'inactive');
    const nearMeVisible = useMemo(
        () => searchPlotsClientSide(nearMe.plots, debounceSearchText),
        [nearMe.plots, debounceSearchText]
    );

    // Fetch initial data when search or filter changes
    useEffect(() => {
        if (nearMe.enabled) return;
        let mounted = true;
        setLoading(true);
        setSkip(0);
        setHasMore(true);

        // Get count for display (reflects applied search and filters)
        getInactivePlotsCount(userProfile, debounceSearchText, filterCriteria).then(count => {
            if (mounted) setTotalCount(count);
        });

        // Get initial list with search and filter criteria
        getInactivePlotsList(userProfile, 0, LIMIT, debounceSearchText, filterCriteria)
            .then(items => {
                if (mounted) {
                    setPlots(items);
                    setHasMore(items.length === LIMIT);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (mounted) {
                    setPlots([]);
                    setLoading(false);
                }
            });

        return () => { mounted = false };
    }, [userProfile, debounceSearchText, filterCriteria, nearMe.enabled]);

    // Load more for infinite scroll
    const loadMore = async (event: CustomEvent<void>) => {
        const newSkip = skip + LIMIT;
        const newItems = await getInactivePlotsList(
            userProfile,
            newSkip,
            LIMIT,
            debounceSearchText,
            filterCriteria
        );

        setPlots((prev: any[]) => [...prev, ...newItems]);
        setSkip(newSkip);
        setHasMore(newItems.length === LIMIT);

        (event.target as HTMLIonInfiniteScrollElement).complete();
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <CustomSearchBox setSearchText={setSearchText} />
                </div>
                {!nearMe.enabled && <IonIcon
                    icon={funnelOutline}
                    onClick={() => setShowFilterModal(true)}
                    style={{ fontSize: '24px', flexShrink: 0, cursor: 'pointer' }}
                />}
            </div>

            <IonItem>
                <IonLabel>Show plots near me</IonLabel>
                <IonToggle
                    id="nearestPlotsToggle"
                    name="nearestPlotsToggle"
                    checked={nearMe.enabled}
                    onIonChange={(e) => nearMe.setEnabled(e.detail.checked)}
                />
            </IonItem>

            {nearMe.enabled ? (
                <>
                    {nearMe.loading && (
                        <>
                            <IonItem lines="none"><IonNote>Finding inactive plots near you…</IonNote></IonItem>
                            <LoadingSkeleton />
                        </>
                    )}
                    {!nearMe.loading && nearMe.error && (
                        <IonItem lines="none">
                            <IonLabel className="ion-text-wrap" color="danger">{nearMe.error}</IonLabel>
                            <IonButton slot="end" fill="clear" onClick={nearMe.retry}>Retry</IonButton>
                        </IonItem>
                    )}
                    {!nearMe.loading && !nearMe.error && (
                        <>
                            <IonItem lines="none">
                                <IonNote slot="end">
                                    {nearMe.truncated
                                        ? `Nearest ${nearMe.limit} · closest first`
                                        : `${nearMeVisible.length} within ${nearMe.maxDistanceKm} km · closest first`}
                                </IonNote>
                            </IonItem>
                            {nearMeVisible.length > 0
                                ? <Plots plots={nearMeVisible} />
                                : <IonItem><IonLabel>{debounceSearchText ? 'No nearby plots match your search' : `No inactive plots within ${nearMe.maxDistanceKm} km`}</IonLabel></IonItem>}
                        </>
                    )}
                </>
            ) : (
                <>
                    {!loading && <IonItem lines="none" >
                        <IonNote slot="end">{plots?.length} of {totalCount} inactive plots</IonNote>
                    </IonItem>
                    }

                    {loading && <LoadingSkeleton />}
                    {!loading && (plots?.length > 0 ? <Plots plots={plots} /> : <IonItem><IonLabel>No plots found</IonLabel></IonItem>)}

                    <IonInfiniteScroll
                        disabled={!hasMore}
                        onIonInfinite={loadMore}
                    >
                        <IonInfiniteScrollContent
                            loadingSpinner="bubbles"
                            loadingText="Loading more plots..."
                        />
                    </IonInfiniteScroll>
                </>
            )}

            <IonModal isOpen={showFilterModal} cssClass="my-custom-class">
                <PlotFilter
                    filter={filterData}
                    filterChangeHandler={setFilterData}
                    onDoneHandler={setFilterCriteria}
                    onCloseHandler={setShowFilterModal}
                    hideFilters={{
                        crop: true,
                        tag: true
                    }}
                ></PlotFilter>
            </IonModal>
        </>

    );
};

export default InactivePlots;

